import type { Session, SupabaseClient, User } from "@supabase/supabase-js"
import { getAuthSessionId } from "@/lib/auth/session-idle"

type LoginClient = Pick<SupabaseClient, "auth" | "from">
type LoginCallbacks = {
  onError: (message: string | null) => void
  onLoading: (loading: boolean) => void
  onNavigate: () => void
  onWarning?: (message: string | null) => void
}

const SESSION_ERROR = "Não foi possível validar sua sessão. Tente entrar novamente."
const PROFILE_ERROR = "Não foi possível consultar seu cadastro. Tente novamente."
export const SESSION_CHECK_UNAVAILABLE_MESSAGE = "Não foi possível verificar sua sessão no momento. Aguarde um instante e tente entrar novamente."
const sessionKey = (session: Session) => `${session.user.id}:${getAuthSessionId(session.access_token) ?? session.access_token}`

// Share one validation path between password login and an existing session.
export function createLoginFlow(client: LoginClient, callbacks: LoginCallbacks, options: { fetch?: typeof fetch } = {}) {
  let active = false
  let generation = 0
  let observedSession: string | null = null
  let submitting = false
  let signingOut = false
  let redirected = false
  let resumeSession = true
  let scheduled: ReturnType<typeof setTimeout> | null = null
  let subscription: { unsubscribe: () => void } | null = null
  const current = (attempt: number) => active && generation === attempt && !redirected
  const cancelScheduled = () => {
    if (scheduled !== null) clearTimeout(scheduled)
    scheduled = null
  }

  const rejectSession = async (attempt: number, message: string) => {
    if (!current(attempt)) return
    signingOut = true
    try {
      await client.auth.signOut({ scope: "local" })
    } catch {
      // Still show the validation error if clearing local credentials fails.
    } finally {
      signingOut = false
    }
    if (!current(attempt)) return
    observedSession = null
    callbacks.onError(message)
    callbacks.onLoading(false)
  }

  const checkSession = async (attempt: number) => {
    const unavailable = () => {
      if (!current(attempt)) return
      resumeSession = false
      callbacks.onWarning?.(SESSION_CHECK_UNAVAILABLE_MESSAGE)
      callbacks.onLoading(false)
    }
    try {
      const response = await (options.fetch ?? fetch)("/api/auth/session-idle", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ activity: false }), cache: "no-store", credentials: "same-origin",
      })
      const status = await response.json()
      if (!current(attempt)) return false
      if (response.status === 401 || status?.expired === true) {
        await rejectSession(attempt, status?.expired
          ? "Sua sessão foi encerrada por inatividade. Entre novamente para continuar."
          : "Sua sessão foi encerrada. Entre novamente para continuar.")
        return false
      }
      if (!response.ok || status?.expired !== false) {
        unavailable()
        return false
      }
      return true
    } catch {
      unavailable()
      return false
    }
  }

  const validate = async (session: Session, attempt: number) => {
    if (!current(attempt)) return
    let user: User | null
    try {
      const result = await client.auth.getUser()
      if (!current(attempt)) return
      user = result.data.user
      if (result.error || !user || user.id !== session.user.id) {
        await rejectSession(attempt, SESSION_ERROR)
        return
      }
    } catch {
      await rejectSession(attempt, SESSION_ERROR)
      return
    }
    try {
      const { data: profile, error: profileError } = await client
        .from("users").select("is_active, role, name").eq("id", user.id).maybeSingle()
      if (!current(attempt)) return
      if (profileError) {
        await rejectSession(attempt, PROFILE_ERROR)
      } else if (!profile) {
        await rejectSession(attempt, "Sua conta foi autenticada, mas não foi possível acessar o perfil da aplicação. Peça ao administrador para verificar o vínculo do cadastro e as permissões de acesso.")
      } else if (!profile.is_active) {
        await rejectSession(attempt, "Usuário desativado. Entre em contato com o administrador.")
      } else if (await checkSession(attempt) && current(attempt)) {
        redirected = true
        callbacks.onNavigate()
      }
    } catch {
      await rejectSession(attempt, PROFILE_ERROR)
    }
  }

  const observeSession = (session: Session | null) => {
    if (!active || !resumeSession || redirected || submitting || (signingOut && !session)) return
    const key = session ? sessionKey(session) : null
    if (key === observedSession) return
    observedSession = key
    const attempt = ++generation
    cancelScheduled()
    if (!session) {
      callbacks.onLoading(false)
      return
    }
    callbacks.onError(null)
    callbacks.onWarning?.(null)
    callbacks.onLoading(true)
    // Supabase invokes this listener while holding its auth lock. Do not call
    // another auth method until that synchronous callback has returned.
    scheduled = setTimeout(() => {
      scheduled = null
      void validate(session, attempt)
    }, 0)
  }

  const stop = () => {
    active = false
    generation++
    cancelScheduled()
    subscription?.unsubscribe()
    subscription = null
  }

  return {
    start(settings: { resumeSession?: boolean } = {}) {
      active = true
      submitting = false
      signingOut = false
      redirected = false
      resumeSession = settings.resumeSession !== false
      observedSession = null
      const attempt = ++generation
      subscription = client.auth.onAuthStateChange((_event, session) => observeSession(session)).data.subscription
      if (!resumeSession) return stop
      void client.auth.getSession().then(({ data: { session }, error }) => {
        if (!current(attempt) || submitting) return
        if (error) {
          callbacks.onError(SESSION_ERROR)
          callbacks.onLoading(false)
          return
        }
        observeSession(session)
      }).catch(() => {
        if (current(attempt)) {
          callbacks.onError(SESSION_ERROR)
          callbacks.onLoading(false)
        }
      })
      return stop
    },
    async signIn(email: string, password: string) {
      if (!active || submitting || redirected) return
      const attempt = ++generation
      submitting = true
      resumeSession = true
      cancelScheduled()
      callbacks.onError(null)
      callbacks.onWarning?.(null)
      callbacks.onLoading(true)
      try {
        const { data: { session }, error } = await client.auth.signInWithPassword({ email: email.trim(), password })
        if (!current(attempt)) return
        if (error) {
          callbacks.onError(error.message === "Invalid login credentials" ? "Email ou senha incorretos" : error.message)
          return
        }
        if (!session) {
          await rejectSession(attempt, SESSION_ERROR)
          return
        }
        observedSession = sessionKey(session)
        submitting = false
        await validate(session, attempt)
      } catch {
        if (current(attempt)) callbacks.onError("Não foi possível conectar ao serviço de autenticação. Tente novamente.")
      } finally {
        if (active && generation === attempt) {
          submitting = false
          if (!redirected) callbacks.onLoading(false)
        }
      }
    },
  }
}
